/**
 * src/components/vendors/VendorImportExport.jsx
 * Route: /vendors/import-export
 */
import { useState, useRef } from 'react';
import toast from 'react-hot-toast';

const API   = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const token = () => localStorage.getItem('iassets_token');

export default function VendorImportExport() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [file,      setFile]      = useState(null);
  const fileRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API}/vendors/excel/export`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `iAssets_Vendors_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Vendor list exported successfully');
    } catch(e) { toast.error('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  const handleImport = async () => {
    if (!file) { toast.error('Please select an Excel file'); return; }
    setImporting(true); setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await fetch(`${API}/vendors/excel/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || JSON.stringify(data));
      setResult(data);
      toast.success(`Import done — ${data.created} created, ${data.updated} updated`);
    } catch(e) { toast.error('Import failed: ' + e.message); }
    finally { setImporting(false); }
  };

  const CARD = { background:'#fff', borderRadius:12, border:'1px solid #e2e8f0',
    boxShadow:'0 1px 4px rgba(0,0,0,.06)' };

  const FIELDS = [
    ['Vendor Code *',     'Unique key — UPDATE if exists, INSERT if new',             true],
    ['Vendor Name *',     'Full vendor/supplier name',                                true],
    ['Contact Person',    'Primary contact name',                                     false],
    ['Designation',       'Contact person\'s designation',                            false],
    ['Email',             'Primary email address',                                    false],
    ['Mobile',            'Mobile number',                                            false],
    ['Phone',             'Office phone',                                             false],
    ['Website',           'Vendor website URL',                                       false],
    ['GST No',            'GST registration number',                                  false],
    ['GST Type',          'Regular / Composition / Unregistered / Consumer',          false],
    ['PAN Number',        'PAN card number',                                          false],
    ['Bank Country',      'Bank\'s country',                                          false],
    ['Bank Name',         'Bank name',                                                false],
    ['Account Number',    'Bank account number',                                      false],
    ['IFSC Code',         'Bank IFSC code',                                           false],
    ['Support Helpline',  'Support phone number',                                     false],
    ['Support Email',     'Support email',                                            false],
    ['Billing Address',   'Full billing address',                                     false],
    ['Shipping Address',  'Full shipping address',                                    false],
    ['Payment Mode',      'Bank Transfer / Cheque / Cash / UPI',                     false],
    ['Vendor Rating',     'Number 0.0 to 5.0',                                       false],
    ['Is Preferred (Y/N)','Y = Preferred vendor, N = Normal',                        false],
    ['Is Active (Y/N)',   'Y = Active, N = Inactive',                                false],
  ];

  return (
    <div style={{ maxWidth:820, fontFamily:'-apple-system,sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:'#1a1f2e', margin:0 }}>
          🏢 Vendor Import / Export
        </h1>
        <p style={{ fontSize:13, color:'#64748b', marginTop:5 }}>
          Export all vendors to Excel · Add or edit records · Re-import to sync database.
        </p>
      </div>

      {/* How it works */}
      <div style={{ ...CARD, padding:'16px 20px', marginBottom:18,
        background:'#f0f6ff', border:'1px solid #bfdbfe' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#1a56db', marginBottom:10 }}>
          📋 How it works
        </div>
        {[
          ['1','Export Excel', 'Downloads all existing vendors pre-filled in the "Vendor Import" sheet.'],
          ['2','Fill / Edit',  'Add new rows or edit existing ones. Yellow columns are required.'],
          ['3','Import',       'Upload the file. Existing Vendor Codes → UPDATE. New codes → INSERT.'],
        ].map(([n, t, d]) => (
          <div key={n} style={{ display:'flex', gap:12, padding:'7px 0',
            borderBottom:'1px solid #dbeafe' }}>
            <span style={{ width:22, height:22, borderRadius:'50%', background:'#1a56db',
              color:'#fff', fontSize:11, fontWeight:800, flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center' }}>{n}</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#1e40af' }}>{t}</div>
              <div style={{ fontSize:12, color:'#475569', marginTop:1 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Export + Import */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>

        <div style={{ ...CARD, padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📤 Export Vendors
          </div>
          <p style={{ fontSize:12, color:'#64748b', lineHeight:1.6, marginBottom:16 }}>
            Downloads an Excel file with all current vendors and an Instructions sheet.
          </p>
          <button onClick={handleExport} disabled={exporting}
            style={{ width:'100%', padding:'12px', border:'none', borderRadius:9,
              background: exporting ? '#93c5fd' : '#1a56db', color:'#fff',
              fontSize:14, fontWeight:700, cursor: exporting ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export to Excel'}
          </button>
        </div>

        <div style={{ ...CARD, padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📥 Import Vendors
          </div>
          <p style={{ fontSize:12, color:'#64748b', lineHeight:1.6, marginBottom:10 }}>
            Upload filled Excel. Matches by <strong>Vendor Code</strong>. Updates existing or creates new.
          </p>
          <div onClick={() => fileRef.current?.click()}
            style={{ border:`2px dashed ${file ? '#1a56db' : '#e2e8f0'}`,
              borderRadius:9, padding:'14px', textAlign:'center',
              cursor:'pointer', marginBottom:10,
              background: file ? '#eff6ff' : '#f8fafc' }}>
            <input ref={fileRef} type="file" accept=".xlsx"
              style={{ display:'none' }}
              onChange={e => { setFile(e.target.files[0]); setResult(null); }}/>
            {file ? (
              <div>
                <div style={{ fontSize:20 }}>📄</div>
                <div style={{ fontSize:12, fontWeight:600, color:'#1a56db' }}>{file.name}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>{(file.size/1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:24 }}>📂</div>
                <div style={{ fontSize:12, color:'#64748b' }}>Click to select .xlsx file</div>
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {file && (
              <button onClick={() => { setFile(null); setResult(null); fileRef.current.value=''; }}
                style={{ padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:8,
                  background:'#fff', fontSize:12, cursor:'pointer', color:'#64748b' }}>✕</button>
            )}
            <button onClick={handleImport} disabled={importing || !file}
              style={{ flex:1, padding:'12px', border:'none', borderRadius:9,
                background: importing ? '#93c5fd' : !file ? '#e2e8f0' : '#16a34a',
                color: !file ? '#94a3b8' : '#fff', fontSize:14, fontWeight:700,
                cursor: (importing || !file) ? 'not-allowed' : 'pointer' }}>
              {importing ? '⏳ Importing…' : '⬆ Import Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div style={{ ...CARD, padding:'20px', marginBottom:20 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:14 }}>
            ✅ Import Result
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            {[
              { label:'Created', val:result.created,    color:'#16a34a', bg:'#f0fdf4' },
              { label:'Updated', val:result.updated,    color:'#1a56db', bg:'#eff6ff' },
              { label:'Skipped', val:result.skipped,    color:'#f59e0b', bg:'#fefce8' },
              { label:'Total',   val:result.total_rows, color:'#374151', bg:'#f8fafc' },
            ].map(s => (
              <div key={s.label} style={{ background:s.bg, borderRadius:9, padding:'12px',
                textAlign:'center', border:`1px solid ${s.color}33` }}>
                <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.val}</div>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {result.errors?.length > 0 && (
            <div style={{ background:'#fff1f2', border:'1px solid #fecaca',
              borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:6 }}>
                ⚠ Errors ({result.errors.length})
              </div>
              <div style={{ fontSize:11, color:'#7f1d1d', fontFamily:'monospace',
                lineHeight:1.8, maxHeight:160, overflowY:'auto' }}>
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Field Reference */}
      <div style={{ ...CARD, padding:'20px' }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#1a1f2e', marginBottom:12 }}>
          📑 Column Reference (23 columns)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
          {FIELDS.map(([k, v, req]) => (
            <div key={k} style={{ display:'flex', gap:8, padding:'5px 9px',
              background: req ? '#fff9e6' : '#f8fafc', borderRadius:6,
              border:`1px solid ${req ? '#fde68a' : '#e2e8f0'}`, fontSize:11 }}>
              <span style={{ fontWeight:700, color: req ? '#dc2626' : '#1a56db',
                flexShrink:0, minWidth:120 }}>{k}:</span>
              <span style={{ color:'#64748b' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
